import {z} from 'zod';
import {SchemaValidationErrorLabel, refineUrl} from '@netx/core-schema';
import {UserLevel} from '@netx/core-user-next/constants';
import {BrandPortalActionType} from '../brandPortalAction';

/*
{
	attributeIds: [-100, -104, -105, -120, -101, -102, -106, -108, -110, -111, -112, -114, -115, 1],
	attributes: [],
	carousel: [],
	category: 1,
	color1: "#1C2638",
	color2: null,
	contactMessage: null,
	copyright: "",
	disableWebfonts: false,
	displayCatDescriptions: false,
	favicon: 0,
	font: 120,
	googleAnalyticsEnabled: false,
	googleAnalyticsId: "",
	group: 0,
	headerLinks: false,
	homeRoute: null,
	id: 1,
	label: "clean",
	logo: 0,
	logoutUrl: null,
	name: "clean",
	path: "clean",
    "properties": {
        "name": "Clean",
        "version": "2.0",
        "menus": [
            {
                "name": "menu1",
                "defaultItems": [
                    {
                        "type": "action",
                        "action": "app:contact"
                    },
                    {
                        "type": "action",
                        "action": "cms:route:category:root"
                    },
                    {
                        "type": "action",
                        "action": "adBuilder:templates:route"
                    }
                ]
            }
        ],
        "pages": [
            {
                "type": "page",
                "title": "home",
                "slug": "home",
                "template": "template1"
            }
        ],
        "homeRoute": "home",
        "templates": {
            "template1": "Template 1",
            "template2": "Template 2",
            "template3": "Template 3",
            "template4": "Template 4",
            "template5": "Template 5",
            "template6": "Template 6",
            "template7": "Template 7",
            "template8": "Template 8",
            "template9": "Template 9",
            "template10": "Template 10",
            "template11": "Template 11"
        }
    },
	requireLogin: false,
	searchPlaceholder: "Search",
	singleSignOnURL: "",
	ssoLoginEnabled: false,
	ssoLoginLabel: null,
	tagline: null,
	termsAndConditions: "",
	theme: "clean",
	userLevel: 0,
	visible: false,
	watermark: 0,
	watermarkPos: "bottom-left",
*/

export enum BrandPortalAttributeContext {
	DETAIL = 'detailAttribute',
	SORT = 'sortAttribute',
}

export enum BrandPortalWatermarkPosition {
	BOTTOM_LEFT = 'bottom-left',
	BOTTOM_RIGHT = 'bottom-right',
	CENTER = 'center',
	TOP_LEFT = 'top-left',
	TOP_RIGHT = 'top-right',
}

export const BrandPortalAttributeSchema = z.object({
	brandPortalId: z.number().int().gt(-1),
	attributeTemplateId: z.number(),
	[BrandPortalAttributeContext.DETAIL]: z.boolean(),
	[BrandPortalAttributeContext.SORT]: z.boolean(),
});

export type IBrandPortalAttribute = z.infer<typeof BrandPortalAttributeSchema>;

export const BrandPortalSchema = z.object({
	id: z.number().int().positive(),
	advancedSearchEnabled: z.boolean(),
	attributeIds: z.array(z.number().int()),
	attributes: z.array(BrandPortalAttributeSchema),
	carousel: z.array(z.number().int().positive()),
	category: z.number().int().gt(0, SchemaValidationErrorLabel.REQUIRED),
	color1: z.string(),
	color2: z.string().nullable(),
	contactMessage: z.string().nullable(),
	copyright: z.string(),
	displayCatDescriptions: z.boolean(),
	disableWebfonts: z.boolean(),
	enableSignIn: z.boolean(),
	favicon: z.number().int().positive(),
	font: z.number().int().positive(),
	googleAnalyticsEnabled: z.boolean(),
	googleAnalyticsId: z.string().nullable(),
	group: z.number().int().positive(),
	headerLinks: z.boolean(),
	/** tied to a `page.slug` */
	homeRoute: z.string(), //.nonempty(SchemaValidationErrorLabel.REQUIRED),
	pageId: z.number().int().gte(-1),
	label: z.string().nonempty(SchemaValidationErrorLabel.REQUIRED),
	logo: z.number().int().positive(),
	logoutUrl: refineUrl(z.string()).nullable(),
	name: z.string().nonempty(SchemaValidationErrorLabel.REQUIRED),
	path: z.string().nonempty(SchemaValidationErrorLabel.REQUIRED),
	properties: z.object({
		name: z.string(),
		homeRoute: z.string(),
		menus: z.array(
			z.object({
				name: z.string(),
				defaultItems: z.array(
					z.object({
						type: z.nativeEnum(BrandPortalActionType),
						action: z.string(),
					}),
				),
			}),
		),
		pages: z.array(
			z.object({
				type: z.string(),
				title: z.string(),
				slug: z.string(),
				template: z.string(),
			}),
		),
		templates: z.object({
			template1: z.literal('Template 1'),
			template2: z.literal('Template 2'),
			template3: z.literal('Template 3'),
			template4: z.literal('Template 4'),
			template5: z.literal('Template 5'),
			template6: z.literal('Template 6'),
			template7: z.literal('Template 7'),
			template8: z.literal('Template 8'),
			template9: z.literal('Template 9'),
			template10: z.literal('Template 10'),
			template11: z.literal('Template 11'),
		}),
		version: z.string(),
	}),
	requireLogin: z.boolean(),
	searchPlaceholder: z.string(),
	singleSignOnURL: refineUrl(z.string()),
	ssoLoginEnabled: z.boolean().nullable(),
	ssoLoginLabel: z.string().nullable(),
	tagline: z.string().nullable(),
	termsAndConditions: z.string(),
	theme: z.string(), // always `clean`
	userLevel: z.nativeEnum(UserLevel),
	visible: z.boolean(),
	watermark: z.number().int().positive(),
	watermarkPos: z.nativeEnum(BrandPortalWatermarkPosition),
});

export const BrandPortalSchemaCheck = (
	{
		googleAnalyticsEnabled,
		googleAnalyticsId,
		ssoLoginEnabled,
		ssoLoginLabel,
		singleSignOnURL,
	}: Partial<IBrandPortal>,
	ctx: z.RefinementCtx,
) => {
	if (googleAnalyticsEnabled === true && !googleAnalyticsId) {
		ctx.addIssue({
			fatal: true,
			code: z.ZodIssueCode.custom,
			path: ['googleAnalyticsId'],
			message: SchemaValidationErrorLabel.REQUIRED,
		});
	}

	if (ssoLoginEnabled === true) {
		if (!singleSignOnURL) {
			ctx.addIssue({
				fatal: true,
				code: z.ZodIssueCode.custom,
				path: ['singleSignOnURL'],
				message: SchemaValidationErrorLabel.REQUIRED,
			});
		}
		if (!ssoLoginLabel) {
			ctx.addIssue({
				fatal: true,
				code: z.ZodIssueCode.custom,
				path: ['ssoLoginLabel'],
				message: SchemaValidationErrorLabel.REQUIRED,
			});
		}
	}
	return ctx;
};

/** helper function for wrapping the schema in all refinements */
export const applyBrandPortalSchemaEffects = <T extends z.ZodRawShape>(
	schema: z.ZodObject<T>,
) => schema.superRefine(BrandPortalSchemaCheck);

/**
 * default refined schema\
 * note: using this directly will not hit any refinements should any of the initial schema validations fail\
 * if you want the refinements to be hit, regardless of the initial schema validation failing then call the
 * `applyBrandPortalSchemaEffects` function directly and supply a `partial` schema
 * @example
 * ```ts
 * applyBrandPortalSchemaEffects(BrandPortalSchema.partial())
 * ```
 */
export const BrandPortalSchemaRefined =
	applyBrandPortalSchemaEffects(BrandPortalSchema);

export type IBrandPortal = z.infer<typeof BrandPortalSchema>;

export const BrandPortalField = BrandPortalSchema.keyof().enum;

export const BrandPortalFormSchema = BrandPortalSchema.pick({
	advancedSearchEnabled: true,
	attributes: true,
	category: true,
	contactMessage: true,
	displayCatDescriptions: true,
	enableSignIn: true,
	googleAnalyticsEnabled: true,
	googleAnalyticsId: true,
	homeRoute: true,
	label: true,
	logoutUrl: true,
	pageId: true,
	path: true,
	requireLogin: true,
	searchPlaceholder: true,
	ssoLoginEnabled: true,
	ssoLoginLabel: true,
	singleSignOnURL: true,
	userLevel: true,
});

/** brand portal form values schema */
export type IBrandPortalFormValues = z.infer<typeof BrandPortalFormSchema>;

export interface IBrandPortalAttributeFormFieldArrayItem extends IBrandPortalAttribute {
	id: string;
}

export const BrandPortalSchemaNew = BrandPortalSchema.pick({
	label: true,
	path: true,
});

/** a new brand portal record */
export type IBrandPortalNew = z.infer<typeof BrandPortalSchemaNew>;
